import { EChartsOption } from "echarts-for-react";
import moment from "moment";
import { RefreshRate } from "types/Charts";
import { AccessTokenScopeOptions } from "./enums";
import { getDateTimeFormat } from "utils/datetime";

export * from "./enums";
export * from "./messages";

export const COLORS = [
    "#F2495E",
    "#F2994A",
    "#F2C94C",
    "#219653",
    "#2F80ED",
    "#56CCF2",
    "#BB6BD9",
    "#41B3AF",
    "#DF54A1",
    "#82C771",
];

export const Patterns = {
    passwordPattern:
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]{8,32}$/,
    namePattern: /^[^\s]+(\s+[^\s]+)*$/,
    dashboardNamePattern: /^.{1,64}$/,
    orgNamePattern: /^([\x20-\x7E]{1,32})$/,
    emailPattern:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    devEUIPattern: /^[0-9A-F]{16}$/,
    eventNamePattern: /^([\x20-\x7E]{1,32}$)/,
    eventVariableNamePattern: /^([A-Za-z_][A-Za-z0-9_]{0,31}$)/,
    phoneNumberPattern: /^\+[1-9][0-9]{7,14}$/,
    macAddressPattern: /\w\w:\w\w:\w\w:\w\w:\w\w:\w\w/,
    uuidPattern: /^([a-zA-Z0-9]{19}$)/,
    gatewayNamePattern: /^([\x20-\x7E]{1,32}$)/,
    serialPattern: /^[a-zA-Z0-9]{8}$/,
    thresholdPattern: /^[-]?(?:[0-9]+(?:\.[0-9]+)?|\.[0-9]+)$/,
    alphanumericPattern: /^[0-9a-zA-Z]+$/,
    asciiPattern: /^[\x20-\x7E]*$/,
    integerPattern: /^[-]?[0-9]+$/,
    floatPattern: /^$|^-$|^[-]?(?:[0-9]+\.?[0-9]*|\.[0-9]*)$/,
    float2DigitsPattern: /^(?:-?\d+(\.\d{1,2})?)$/,
    zipCodePattern:
        /^(?!.*-$)(?!-.*$)[a-zA-Z0-9][a-zA-Z0-9- ]{2,7}[a-zA-Z0-9]$/,
    hasLeadingSpaces: /^\s/,
    hasTrailingSpaces: /\s$/,
    timeScheduleEveryPattern:
        /^([1-9]|[1-9][0-9]|[1-6][0-9]{2}|7[01][0-9]|720)$/,
    repeatDailyPattern: /^([1-9]|[1-9][0-9]|1[0-7][0-9]|18[0-2])$/,
    repeatWeeklyPattern: /^([1-9]|1[0-9]|2[0-6])$/,
    repeatMonthlyPattern: /^([1-6])$/,
    repeatYearlyPattern: /^([1-9]|10)$/,
    chartRefreshRatePattern:
        /^([5-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-2][0-9][0-9][0-9]|3[0-5][0-9][0-9]|3600)$/,
    timePattern: /^(0[0-9]|[1-5][0-9])$/,
    codePattern: /^\d{6}$/,
};

export const TimeGapToNowList = [
    {
        min: 0,
        max: 86400, // <= 24h
    },
    {
        min: 86401, // > 24h
        max: 604799, // < 7d
    },
    {
        min: 604800, // >= 7d
    },
];

export const LastSixMonthsTimeSelection = [
    {
        time: "Last 5 Minutes",
        showAggregate: false,
        timeGapToNow: 300,
        hours: 5 / 60,
    },
    {
        time: "Last 15 Minutes",
        showAggregate: false,
        timeGapToNow: 900,
        hours: 0.25,
    },
    {
        time: "Last 30 Minutes",
        showAggregate: false,
        timeGapToNow: 1800,
        hours: 0.5,
    },
    {
        time: "Last 1 Hour",
        showAggregate: false,
        timeGapToNow: 3600,
        hours: 1,
    },
    {
        time: "Last 6 Hours",
        showAggregate: true,
        INTVL: 3600000,
        timeGapToNow: 21600,
        hours: 6,
    },
    {
        time: "Last 12 Hours",
        showAggregate: true,
        INTVL: 3600000,
        timeGapToNow: 43200,
        hours: 12,
    },
    {
        time: "Last 24 Hours",
        showAggregate: true,
        INTVL: 3600000,
        timeGapToNow: 86400,
        hours: 24,
    },
    {
        time: "Last 7 Days",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 604800,
        hours: 168,
    },
    {
        time: "Last 15 Days",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 1296000,
        hours: 360,
    },
    {
        time: "Last 1 Month",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 2592000,
        hours: 720,
    },
    {
        time: "Last 3 Months",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 7776000,
        hours: 2160,
    },
    {
        time: "Last 6 Months",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 15552000,
        hours: 4320,
    },
];

export const LastTimeSelection = [
    {
        time: "Last 15 minutes",
        showAggregate: false,
        timeGapToNow: 900,
        hours: 0.25,
    },
    {
        time: "Last 30 minutes",
        showAggregate: false,
        timeGapToNow: 1800,
        hours: 0.5,
    },
    {
        time: "Last 1 hour",
        showAggregate: false,
        timeGapToNow: 3600,
        hours: 1,
    },
    {
        time: "Last 24 hours",
        showAggregate: true,
        INTVL: 3600000,
        timeGapToNow: 86400,
        hours: 24,
    },

    {
        time: "Last 1 month",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 2592000,
        hours: 720,
    },
    {
        time: "Last 3 months",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 7776000,
        hours: 2160,
    },
    {
        time: "Last 6 months",
        showAggregate: true,
        INTVL: 86400000,
        timeGapToNow: 15552000,
        hours: 4320,
    },
];

export const LastTimeSelectionStatusChart = [
    { time: "1 Day", dataValue: "1d", totalMinutes: "1440" },
    { time: "1 Week", dataValue: "7d", totalMinutes: "10080" },
    { time: "1 Month", dataValue: "30d", totalMinutes: "43200" },
    { time: "1 Year", dataValue: "1y", totalMinutes: "525600" },
];

export const DaysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
];

export const DaysMap: any = {
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
};

export const MonthsMap: any = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
};

export const SecondsToCountdown = 30;

export const DeviceTypeDict = {
    sensor: {
        label: "sensor",
    },
    actuator: {
        label: "actuator",
    },
};

export const LDSUTypeDict = {
    LDSU: {
        label: "LDSU",
    },
};

export const InputNumberPatterns: { [key: string]: RegExp } = {
    float: Patterns.thresholdPattern,
    integer: Patterns.integerPattern,
};

export const DefaultMinCharacter = 1;
export const DefaultMaxCharacter = 32;
export const HeartbeatUpdateAfter = 120;
export const EventNameMaxCharacter: number = DefaultMaxCharacter;

export const HttpStatus = {
    OK: 200,
    CREATED: 201,
    ACCEPTED: 202,
    NO_CONTENT: 204,
    MOVED_PERMANENTLY: 301,
    MULTIPLE_CHOICES: 300,
    FOUND: 302,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    PAYMENT_REQUIRED: 402,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    UNPROCESSABLE_ENTITY: 422,
    TOO_MANY_REQUESTS: 429,
    INTERNAL_SERVER_ERROR: 500,
    NOT_IMPLEMENTED: 501,
    BAD_GATEWAY: 502,
    SERVICE_UNAVAILABLE: 503,
};
export const UnknownSensor = "Unknown Sensor";

export const UnknownActuator = "Unknown Actuator";

export const MaxActuatorLimit = 3600;

export const MAX_LOOP_LIMIT = 65535;

export const MaxNameChar = 255;

export const MinNameChar = 1;

//manual sync https://brtchip.atlassian.net/wiki/spaces/IOT/pages/674531860/Standardize+Notice+Message#2.1-Replace-based-on-dictionary
export const ServerResReplaceDict: { [key: string]: string } = {
    gateway_id: "UUID",
    mac: "Ethernet MAC Address",
    serial: "Serial Number",
    ranges: "Time Schedule",
    for_: "Duration",
    seconds: "Delay Time",
    extended: "Building",
    project: "PRN",
    // 1.5.9I
    "information.name": "Organisation Name",
    "information.ownerNameDisplay": "Owner Name",
    "information.nickName": "Organisation Nickname",
};

export const HighlightList = [
    {
        key: "email",
        pattern:
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/gm,
    },
    {
        key: "role_string",
        pattern: /^(\d+.Role\(s\))/gi,
    },
    ...["Organisation ", "The member", "Invitation "].map((k) => ({
        key: k,
        pattern: new RegExp(`^${k}`, "gi"),
    })),
    // all replace text serverResReplaceDict
];

export const INFINITE = "INFINITE";
export const COUNTED = "COUNTED";

export const AUTO_MODE = "AUTO";
export const MANUAL_MODE = "MANUAL";
export const PULSE_MODE = "PULSE";
export const LEVEL_MODE = "LEVEL";
export const LOOP_MODE = "LOOP";
export const PWM_MODE = "PWM";
export const ANALOG_MODE = "ANALOG";
export const MOTOR_MODE = "MOTOR";

// Just for testing, consider to remove later
export const fourWireMotorType = "4 Wire Motor";

export const TimeSchedule = {
    ALL_DAY: "all_day",
    AT: "at",
    BETWEEN: "between",
    BEFORE_OR_AFTER: "before_or_after",
    BEFORE: "before",
    AFTER: "after",
    EVERY: "every",
};

export const DAILY = "daily";
export const WEEKLY = "weekly";
export const MONTHLY = "monthly";
export const YEARLY = "yearly";

export const ERROR_NOT_POSITIVE_INTEGER = "error_not_positive_integer";
export const ERROR_NOT_INTEGER = "error_not_integer";
export const ERROR_NO_INTERVAL = "error_no_interval";
export const ERROR_INVALID_DATE = "error_invalid_date";
export const ERROR_NO_DURATION =
    "Please enter a value for time schedule 'Every'.";
export const ERROR_INVALID = "invalid";
export const ERROR_EMPTY = "error_empty";
export const ERROR_INCORRECT_VALUE = "error_incorrect_value";
export const ERROR_DUPLICATE = "duplicate";
export const ERROR_INVALID_BEFORE_AFTER_DATE =
    "error_invalid_before_after_date";

export enum SubscriptionStatus {
    ACTIVE = "ACTIVE",
    CANCELED = "CANCELED",
    EXPIRED = "EXPIRED",
    PENDING = "PENDING",
    CREATED = "CREATED",
    PAST_DUE = "PAST_DUE",
}

export enum FESubscriptionStatus {
    SUBSCRIBED = "subscribed",
    RESUBSCRIBED = "resubscribed",
    PENDING_CANCEL = "pendingCancel",
    CANCELLATION_PERIOD_ENDED = "cancellationPeriodEnded",
    EXPIRED = "expired",
    UNSUBSCRIBED = "unsubscribed",
    PAST_DUE = "pastDue",
}

export enum TokenType {
    ALLOCATED = "ALLOCATED",
    BONUS = "BONUS",
    PURCHASED = "PURCHASED",
}

export enum PlanType {
    SUBSCRIPTION = "SUBSCRIPTION",
}

export enum CheckoutType {
    PLAN = "plan",
    ADDON = "addon",
}

export const Operator = {
    OUT_OF_RANGE: ["<", "or", ">"],
    WITHIN_RANGE: [">", "and", "<"],
    LESS_THAN: "<",
    GREATER_THAN: ">",
    EQUAL_TO: "==",
    LESS_THAN_EQUAL_TO: "<=",
    GREATER_THAN_EQUAL_TO: ">=",
};

export const ConditionOptions = {
    ONCE_TRUE: "when-true",
    ONCE_FALSE: "when-false",
    ONCE_LESS_THAN: "when-<",
    ONCE_GREATER_THAN: "when->",
    ONCE_EQUAL_TO: "when-==",
    CONTINUES_TRUE: "is-true",
    CONTINUES_FALSE: "is-false",
    CONTINUES_LESS_THAN: "is-<",
    CONTINUES_GREATER_THAN: "is->",
    CONTINUES_EQUAL_TO: "is-==",
};

export const ConditionInfos: any = {
    "when-true": {
        label: "When true",
        note: "The condition 'When true' will trigger the event only once when the value becomes true.",
    },
    "when-false": {
        label: "When false",
        note: "The condition 'When false' will trigger the event only once when the value becomes false.",
    },
    "is-true": {
        label: "Is true",
        note: "The condition 'Is true' will trigger the event continuously as long as the value is true.",
    },
    "is-false": {
        label: "Is false",
        note: "The condition 'Is false' will trigger the event continuously as long as the value is false.",
    },
    "when-<": {
        label: "When less than",
        note: "The condition 'When less than' will trigger the event only once when the value drops below $value.",
    },
    "when->": {
        label: "When greater than",
        note: "The condition 'When greater than' will trigger the event only once when the value exceeds $value.",
    },
    "when-==": {
        label: "When equal to",
        note: "The condition 'When equal to' will trigger the event only once when the value reaches $value.",
    },
    "is-<": {
        label: "Is less than",
        note: "The condition 'Is less than' will trigger the event continuously as long as the value is less than $value.",
    },
    "is->": {
        label: "Is greater than",
        note: "The condition 'Is greater than' will trigger the event continuously as long as the value is greater than $value.",
    },
    "is-==": {
        label: "Is equal to",
        note: "The condition 'Is equal to' will trigger the event continuously as long as the value is equal to $value.",
    },
};

export enum SensorThreshold {
    SINGLE_THRESHOLD = `SINGLE_THRESHOLD`,
    DUAL_THRESHOLD = `DUAL_THRESHOLD`,
    NO_THRESHOLD = `NO_THRESHOLD`,
}

export enum SensorAlertMode {
    ALERT_ONCE = "ALERT_ONCE",
    ALERT_CONTINUOUSLY = "ALERT_CONTINUOUSLY",
}

export enum AlertPeriod {
    ALERT_CONTINUOUSLY_MIN = 30,
    ALERT_CONTINUOUSLY_MAX = 1800,
    ALERT_ONCE_MAX = 86400,
}

export const SensorUnitText = {
    C: "°C",
    "%": "%",
    na: "",
};

export const billingStartDate = moment().format("DD MMM YYYY");

export const CharacterLimit = {
    EVENT_SMS_MESSAGE_MAX: 280,
    EVENT_EMAIL_SUBJECT_MAX: 280,
    EVENT_EMAIL_MESSAGE_MAX: 4096,
    EVENT_NOTIFICATION_MESSAGE_MAX: 280,
    SENSOR_REPORT_RATE_MAX: 1800,
    LDSU_NAME_MAX: 32,
    UART_SUBJECT_MAX: 280,
    UART_MESSAGE_MAX: 280,
    ACTUATOR_NAME_MAX: 32,
};

export const ConfigLimit = {
    SENSOR_REPORT_RATE_MIN: 5,
    SENSOR_REPORT_RATE_MAX: 1800,
};

export const EventConfigLimit = {
    REPEAT_DAILY_MAX: 182,
    REPEAT_WEEKLY_MAX: 26,
    REPEAT_MONTHLY_MAX: 6,
    REPEAT_YEARLY_MAX: 10,
    TIME_SCHEDULE_MINUTES_MAX: 720,
};

export const DeactivateOption: any = {
    PULSE: ["Immediate", "Immediately after T1", "Immediately after T2"],
    LOOP: ["Immediate", "Immediately after T1", "Immediately after T2"],
    PWM: [
        "Immediate",
        "Immediately after T1",
        "Immediately after T2",
        "Immediately after current cycle",
    ],
};

export const DeactivateOptionsLookup: { [key: string]: string } = {
    Immediate: "IMMEDIATE",
    "Immediately after T1": "AFTER_T1",
    "Immediately after T2": "AFTER_T2",
    "Immediately after current cycle": "AFTER_CYCLE",
};

export const SUBSCRIPTION_DATE_FORMAT: string = "DD MMM YYYY";

export const SubscriptionDict: any = {
    prepaid: "IoTPortal Prepaid Subscription",
    monthly: "IoTPortal Monthly Subscription",
    addon: "Bundle of 1,000,000 Purchased Tokens",
    basic: "IoTPortal Basic Subscription",
};

export const BillingHistoryDict: any = {
    monthly_plan: "Monthly Subscription",
    token_1m: "Purchased Token",
};
export const DASHBOARD_CHART_MAX = 16;

export const BaudRateOptions: string[] = [
    "1200",
    "9600",
    "19200",
    "38400",
    "57600",
    "115200",
    "230400",
];

export const ParityOptions: string[] = ["None", "Odd", "Even"];

export const InputDateTimeFormat = "DD/MM/YYYY HH:mm";

export const ExpirationAccessTokenOptions: number[] = [
    7, 15, 30, 60, 90, 180, 365, 1000,
];

export const AccessKeyScope: any = {
    [AccessTokenScopeOptions.SENSOR]: {
        scopeHeader: "Retrieve gateway, LDSU details and sensor data",
        scopeDescription:
            "Retrieve detailed information about gateways, LDSUs and their status, and sensor data.",
    },
    [AccessTokenScopeOptions.ACTUATOR]: {
        scopeHeader: "Send commands to actuator",
        scopeDescription: "Control actuators by sending specific commands.",
    },
};

export const DATE_FORMAT: string = "DD/MM/yyyy";

// 1280: Motion Sensor, 1281 - Generic Digital Input
export const BinarySensorsCLSList: string[] = ["1280", "1281"];

// 2304: pH, 2305: EC, 2306: DO, 2307: Salinity, 2308: ORP
export const SensorAssociationCLS = ["2304", "2305", "2306", "2307", "2308"];

// For 1.5.4, only "Temperature" sensor (cls:256) can be associated.
export const CanBeAssociatedSensorsCLS = ["256"];

export const FirmwareStatusLookup: { [key: string]: string } = {
    FAILED: "Fail",
    IN_PROGRESS: "In-progress",
    COMPLETED: "Success",
};

export const UNGROUPED_NAME = "ungrouped";

export const UNGROUPED_DISPLAY_NAME = "Standalone";

export const DEFAULT_OPTION: EChartsOption = {
    backgroundColor: "#2F3A51",
    tooltip: {
        trigger: "axis",
    },
    title: {
        show: true,
        left: 14,
        top: 15,
        text: "Unknown Sensor",
        textStyle: {
            color: "#ffffff",
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: 18,
            overflow: "truncate",
            ellipsis: "...",
        },
        subtext: "Unknown Sensor",
        subtextStyle: {
            color: "#828FAB",
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: 14,
            overflow: "truncate",
            ellipsis: "...",
            rich: {
                s1: {
                    color: "#828fab",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 14,
                },
                s2: {
                    color: "#F2495E",
                    fontSize: 18,
                    fontWeight: "bold",
                },
                s3: {
                    color: "white",
                    fontSize: 18,
                    fontFamily: "Roboto",
                },
                s4: {
                    fontSize: 6,
                },
                s5: {
                    color: "#828fab",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 18,
                },
            },
        },
    },
    toolbox: {
        feature: {
            dataZoom: {
                yAxisIndex: "none",
                title: {
                    back: "Zoom Out",
                },
            },
            restore: {},
            saveAsImage: {
                icon: "path://M8.79999 3.71991C7.92975 3.71991 7.07906 3.97797 6.35548 4.46144C5.63191 4.94492 5.06795 5.63211 4.73493 6.4361C4.4019 7.2401 4.31477 8.12479 4.48454 8.97831C4.65432 9.83182 5.07337 10.6158 5.68872 11.2312C6.30407 11.8465 7.08808 12.2656 7.94159 12.4354C8.79511 12.6051 9.6798 12.518 10.4838 12.185C11.2878 11.852 11.975 11.288 12.4584 10.5644C12.9419 9.84084 13.2 8.99015 13.2 8.11991C13.2 6.95296 12.7364 5.8338 11.9113 5.00864C11.0861 4.18348 9.96694 3.71991 8.79999 3.71991ZM8.79999 11.6399C8.1038 11.6399 7.42324 11.4335 6.84438 11.0467C6.26552 10.6599 5.81436 10.1101 5.54794 9.46695C5.28152 8.82376 5.21181 8.116 5.34763 7.43319C5.48345 6.75038 5.8187 6.12317 6.31098 5.63089C6.80326 5.13861 7.43046 4.80336 8.11327 4.66754C8.79608 4.53172 9.50384 4.60143 10.147 4.86785C10.7902 5.13427 11.34 5.58544 11.7268 6.1643C12.1135 6.74316 12.32 7.42372 12.32 8.11991C12.32 9.05347 11.9491 9.9488 11.289 10.6089C10.6289 11.2691 9.73355 11.6399 8.79999 11.6399Z M14.52 1.95998H12.5928L12.2276 1.22958C12.0602 0.930245 11.8192 0.678578 11.5274 0.498406C11.2356 0.318234 10.9026 0.215518 10.56 0.199982H7.04C6.69739 0.215518 6.36442 0.318234 6.0726 0.498406C5.78079 0.678578 5.53978 0.930245 5.3724 1.22958L5.0072 1.95998H3.08C2.26313 1.95998 1.47972 2.28448 0.902111 2.86209C0.324499 3.4397 0 4.22311 0 5.03998V12.08C0 12.8968 0.324499 13.6803 0.902111 14.2579C1.47972 14.8355 2.26313 15.16 3.08 15.16H14.52C15.3369 15.16 16.1203 14.8355 16.6979 14.2579C17.2755 13.6803 17.6 12.8968 17.6 12.08V5.03998C17.6 4.22311 17.2755 3.4397 16.6979 2.86209C16.1203 2.28448 15.3369 1.95998 14.52 1.95998ZM16.72 12.08C16.72 12.6635 16.4882 13.223 16.0756 13.6356C15.663 14.0482 15.1035 14.28 14.52 14.28H3.08C2.49652 14.28 1.93694 14.0482 1.52436 13.6356C1.11178 13.223 0.88 12.6635 0.88 12.08V5.03998C0.88 4.4565 1.11178 3.89693 1.52436 3.48435C1.93694 3.07177 2.49652 2.83998 3.08 2.83998H5.28C5.36125 2.83973 5.44085 2.81699 5.50997 2.77428C5.57909 2.73156 5.63503 2.67054 5.6716 2.59798L6.16 1.62558C6.25235 1.47089 6.38047 1.34061 6.53359 1.24568C6.68671 1.15074 6.86037 1.09392 7.04 1.07998H10.56C10.7396 1.09392 10.9133 1.15074 11.0664 1.24568C11.2195 1.34061 11.3476 1.47089 11.44 1.62558L11.9284 2.59798C11.965 2.67054 12.0209 2.73156 12.09 2.77428C12.1591 2.81699 12.2387 2.83973 12.32 2.83998H14.52C15.1035 2.83998 15.663 3.07177 16.0756 3.48435C16.4882 3.89693 16.72 4.4565 16.72 5.03998V12.08Z",
            },
        },
        top: 12,
        right: 50,
        emphasis: {
            iconStyle: {
                textPosition: "top",
                textPadding: -4,
            },
        },
        iconStyle: {
            borderColor: "#00CBAF",
        },
    },
    legend: {
        data: [],
        selectedMode: true,
        left: 15,
        bottom: 10,
        textStyle: {
            color: "#ffffff",
            fontFamily: "Roboto",
            fontWeight: 400,
        },
    },
    grid: {
        left: 70,
        top: 135,
        right: 70,
        bottom: 120,
    },
    xAxis: {
        type: "time",
        boundaryGap: false,
        axisLine: {
            lineStyle: {
                color: "#4B5569",
            },
        },
        splitNumber: "3",
    },
    yAxis: {
        splitLine: {
            show: true,
            lineStyle: {
                color: "#4B5569",
            },
        },
    },
    series: [],
};

export const DEFAULT_ZOOM_OPTION: EChartsOption = {
    dataZoom: [
        {
            type: "inside",
            start: 0,
            end: 100,
        },
        {
            type: "slider",
            realtime: true,
            start: 0,
            end: 100,
            zoomLock: false,
            textStyle: {
                overflow: "break",
                width: 60,
                fontSize: 10,
                lineHeight: 15,
            },
            bottom: 50,
            labelFormatter: (val: number) =>
                moment(new Date(val), "x").format(getDateTimeFormat(true)),
        },
    ],
};

export const START_OF_DAY_AT_FIRST_MINUTE = moment()
    .startOf("day")
    .set({ hour: 0, minute: 1, second: 0, millisecond: 0 });

export const refreshRateArr: RefreshRate[] = [
    { label: "Pause", seconds: 0 },
    { label: "Auto", seconds: "auto" },
];

export const defaultChartRefreshRate = 10;

export const CHART_CUSTOM_RANGE_OPTION = "Custom Range";
export const COMPANY_LINK = "https://brtsys.com/";
export const PRIVACY_POLICY_LINK = "https://brtsys.com/privacy-policy/";
export const PRODUCT_INFO_LINK = "https://brtsys.com/iotportal/";
export const SOFTWARE_LICENSE_AGREEMENT_LINK =
    "https://brtsys.com/software-license-agreement/";
export const TERMS_OF_SERVICE_LINK = "https://brtsys.com/terms-of-service/";
export const DATE_MONTH_YEAR_FORMAT = "DD/MM/YYYY";

export const MAX_TERMINAL_VALUE = "65535";
export const MIN_TERMINAL_VALUE = "1";
export const MAX_SECONDS_PER_DAY = "86400";
export const MAX_STEP_SIZE_VALUE = "65535";

export const variableType = "VARIABLE";
export const EVENT_ACTIVE_KEY = "activeKey";
export const EVENT_VARIABLE_TIMER_FORMAT = "HH:mm:ss";
export const OVERFLOW_DEFAULT = true;
export const STEP_DEFAULT = "1";
export const SECONDS_PER_DAY = 86400;
export const ONE_HOUR_IN_SECONDS = 3600;
export const RECURRENCE_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const PANEL_TOKEN_ZERO_TOKEN = "zero_token";
export const PANEL_DATE_TIME_FORMAT = `DD/MM/YYYY HH:mm:ss`;

export const MOTOR_CONTROLS = {
    FORWARD: "forward",
    REVERSE: "reverse",
    PAUSE: "pause",
};
export const ALL_MONTHS_LIST = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

export const MAX_FILE_SIZE_MB = 1;

export const DATE_TIME_FORMATS = {
    SHORT_DT_24H: "DD/MM/YYYY HH:mm",
    SHORT_DT_12H: "DD/MM/YYYY hh:mm A",
    LONG_DT_24H: "DD/MM/YYYY HH:mm:ss",
    LONG_DT_12H: "DD/MM/YYYY hh:mm:ss A",
    DATE_ONLY: "DD/MM/YYYY",
    SHORT_TIME_24H: "HH:mm",
    SHORT_TIME_12H: "hh:mm A",
    LONG_TIME_24H: "HH:mm:ss",
    LONG_TIME_12H: "hh:mm:ss A",
    INPUT_TIME_FORMAT: "h:mm A",
    INPUT_SHORT_TIME_24H: "H:mm",
    INPUT_SHORT_TIME_12H: "h:mm A",
};
